import { useState } from 'react';
import type { AdminAccountDetail, AdminPasswordReset } from '@mantelpiece/shared';
import { api } from '../lib/api';
import { formatAgo, formatBytes, formatCount, formatDateTime } from '../lib/format';
import { href } from '../lib/route';
import { useLoad } from '../lib/useLoad';
import { AccountLink, Crumb, Fact, Loading, Pill, YesNo } from '../components/bits';
import { PhotoGrid } from '../components/PhotoGrid';
import { PageHead, Section } from '../components/Shell';
import { AuditList } from './Audit';

interface Props {
  readonly token: string;
  readonly id: string;
  /** The operator's own account id, so the page can say when it is theirs. */
  readonly selfId: string;
}

export const Account = ({ token, id, selfId }: Props) => {
  const loaded = useLoad(() => api.getAccount(token, id), [token, id]);
  const [reset, setReset] = useState<AdminPasswordReset | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const act = async (label: string, work: () => Promise<string | null>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const message = await work();
      setNotice(message ?? `${label} done.`);
      loaded.reload();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : `${label} failed.`);
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = (account: AdminAccountDetail) =>
    act('Password reset', async () => {
      if (
        !window.confirm(
          `Set a temporary password for ${account.email}? Every session they have will be signed out.`,
        )
      ) {
        return 'Nothing changed.';
      }
      const result = await api.resetPassword(token, account.id);
      setReset(result);
      return null;
    });

  return (
    <>
      <Crumb label="Accounts" to="/accounts" />
      <Loading loaded={loaded}>
        {(account) => {
          const now = Date.now();
          const locked = account.lockedUntil !== null && account.lockedUntil > now;
          return (
            <div className="stack-loose">
              <PageHead
                eyebrow={account.id === selfId ? 'Account · this is you' : 'Account'}
                title={account.name}
                lede={
                  <>
                    {account.email}
                    {account.isAdmin && <Pill tone="ok">admin</Pill>}
                    {locked && <Pill tone="bad">locked</Pill>}
                  </>
                }
                actions={
                  <>
                    <button
                      type="button"
                      className="button button-quiet button-small"
                      disabled={busy}
                      onClick={() => void resetPassword(account)}
                    >
                      Reset password
                    </button>
                    {(locked || account.failedCount > 0) && (
                      <button
                        type="button"
                        className="button button-quiet button-small"
                        disabled={busy}
                        onClick={() =>
                          void act('Unlock', async () => {
                            await api.unlockAccount(token, account.id);
                            return 'Unlocked. They can try again now.';
                          })
                        }
                      >
                        Unlock
                      </button>
                    )}
                    <button
                      type="button"
                      className="button button-quiet button-small"
                      disabled={busy || account.liveSessions === 0}
                      onClick={() =>
                        void act('Sign out everywhere', async () => {
                          if (!window.confirm(`Sign ${account.email} out of every device?`)) return 'Nothing changed.';
                          const result = await api.signOutEverywhere(token, account.id);
                          return `Signed out of ${result.sessionsRevoked} session${result.sessionsRevoked === 1 ? '' : 's'}.`;
                        })
                      }
                    >
                      Sign out everywhere
                    </button>
                  </>
                }
              />

              {error && (
                <p className="alert" role="alert">
                  {error}
                </p>
              )}
              {notice && (
                <p className="alert" data-tone="ok" role="status">
                  {notice}
                </p>
              )}
              {reset && (
                <div className="alert" data-tone="ok" role="status">
                  <p>
                    <strong>Temporary password set.</strong> Read it to them now — it is not stored and
                    will not be shown again. {reset.sessionsRevoked} session
                    {reset.sessionsRevoked === 1 ? ' was' : 's were'} signed out. They should change it
                    from their account page once they are in.
                  </p>
                  <p className="copyline">
                    <code>{reset.password}</code>
                    <button
                      type="button"
                      className="button button-quiet button-small"
                      onClick={() => void navigator.clipboard?.writeText(reset.password)}
                    >
                      Copy
                    </button>
                    <button
                      type="button"
                      className="button button-quiet button-small"
                      onClick={() => setReset(null)}
                    >
                      Dismiss
                    </button>
                  </p>
                </div>
              )}

              <Section title="Details">
                <EditDetails
                  token={token}
                  account={account}
                  busy={busy}
                  onSaved={() => {
                    setNotice('Saved.');
                    loaded.reload();
                  }}
                  onError={setError}
                />
                <dl className="facts">
                  <Fact label="Joined">{formatDateTime(account.createdAt)}</Fact>
                  <Fact label="Last seen">{formatAgo(account.lastSeenAt, now)}</Fact>
                  <Fact label="Live sessions">{formatCount(account.liveSessions)}</Fact>
                  <Fact label="Failed sign-ins">
                    {formatCount(account.failedCount)}
                    {account.failedAt !== null && <> · last {formatAgo(account.failedAt, now)}</>}
                  </Fact>
                  <Fact label="Locked until">{locked ? formatDateTime(account.lockedUntil) : '—'}</Fact>
                  <Fact label="Library">
                    {formatCount(account.assets)} pictures, {formatBytes(account.assetBytes)}
                  </Fact>
                  <Fact label="Id">
                    <span className="mono">{account.id}</span>
                  </Fact>
                </dl>
              </Section>

              <Section title={`Frames they own (${account.owned.length})`}>
                {account.owned.length === 0 ? (
                  <p className="empty">None.</p>
                ) : (
                  <ul className="ledger">
                    {account.owned.map((device) => (
                      <li key={device.id}>
                        <div className="ledger-main">
                          <a className="ledger-name" href={href(`/frames/${device.id}`)}>
                            {device.label}
                          </a>
                          <span className="ledger-meta">
                            {formatCount(device.photoCount)} photos · {formatCount(device.pendingCount)} pending ·{' '}
                            {formatCount(device.screenCount)} screens
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title={`Frames shared with them (${account.memberOf.length})`}>
                {account.memberOf.length === 0 ? (
                  <p className="empty">None.</p>
                ) : (
                  <ul className="ledger">
                    {account.memberOf.map((m) => (
                      <li key={m.id}>
                        <div className="ledger-main">
                          <a className="ledger-name" href={href(`/frames/${m.id}`)}>
                            {m.label}
                            <Pill>{m.role}</Pill>
                          </a>
                          <span className="ledger-meta">
                            owned by <AccountLink account={m.owner} /> · joined {formatAgo(m.joinedAt, now)} ·
                            send <YesNo value={m.canSend} /> · auto-accept <YesNo value={m.autoAccept} />
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title={`Albums (${account.albumList.length})`}>
                {account.albumList.length === 0 ? (
                  <p className="empty">None.</p>
                ) : (
                  <ul className="ledger">
                    {account.albumList.map((al) => (
                      <li key={al.id}>
                        <div className="ledger-main">
                          <span className="ledger-name">{al.label}</span>
                          <span className="ledger-meta">
                            {formatCount(al.itemCount)} pictures · on {formatCount(al.publishedCount)} frames ·
                            updated {formatAgo(al.updatedAt, now)}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section
                title="Recent photos they sent"
                aside={<a href={href('/photos', { accountId: account.id, filter: 'all' })}>All →</a>}
              >
                <PhotoGrid token={token} photos={account.recentPhotos} onChanged={loaded.reload} />
              </Section>

              <Section title="Sessions">
                {account.sessions.length === 0 ? (
                  <p className="empty">Never signed in.</p>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th scope="col">Started</th>
                          <th scope="col">Last seen</th>
                          <th scope="col">Expires</th>
                          <th scope="col">State</th>
                        </tr>
                      </thead>
                      <tbody>
                        {account.sessions.map((s) => {
                          const expired = s.expiresAt !== null && s.expiresAt < now;
                          return (
                            <tr key={s.id}>
                              <td>{formatDateTime(s.createdAt)}</td>
                              <td>{formatAgo(s.lastSeenAt, now)}</td>
                              <td>{formatDateTime(s.expiresAt)}</td>
                              <td>
                                {s.revokedAt !== null ? (
                                  <Pill tone="bad">signed out {formatAgo(s.revokedAt, now)}</Pill>
                                ) : expired ? (
                                  <Pill>expired</Pill>
                                ) : (
                                  <Pill tone="ok">live</Pill>
                                )}
                                {s.current && <Pill tone="wait">this one</Pill>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              <Section title="What administrators have done to this account">
                <AuditList entries={account.audit} />
              </Section>
            </div>
          );
        }}
      </Loading>
    </>
  );
};

interface EditProps {
  readonly token: string;
  readonly account: AdminAccountDetail;
  readonly busy: boolean;
  readonly onSaved: () => void;
  readonly onError: (message: string) => void;
}

const EditDetails = ({ token, account, busy, onSaved, onError }: EditProps) => {
  const [name, setName] = useState(account.name);
  const [email, setEmail] = useState(account.email);
  const [saving, setSaving] = useState(false);
  const dirty = name.trim() !== account.name || email.trim() !== account.email;

  const save = async () => {
    setSaving(true);
    try {
      await api.editAccount(token, account.id, { name: name.trim(), email: email.trim() });
      onSaved();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="row"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <label className="field">
        <span>Name</span>
        <input className="input" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="field">
        <span>Email</span>
        <input
          className="input"
          type="email"
          value={email}
          maxLength={254}
          autoCapitalize="off"
          spellCheck={false}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      <button type="submit" className="button button-small" disabled={busy || saving || !dirty}>
        {saving ? 'Saving' : 'Save'}
      </button>
    </form>
  );
};
